import type { FC } from 'react'
import { Chip } from '@truepill/react-capsule'
import { useQuery } from '@truepill/tpos-react-router'
import type { CustomDurSubject, Medispan } from '@truepill/tpos-types'
import DropDownOption from 'components/DropDownOption'
import { GET_DUR_ALLERGIES } from 'gql'
import useStateWithDebouncedValue from 'hooks/useStateWithDebouncedValue'
import { capsulePrimaryColorDark, darkOrange, lightOrange } from 'styles/styleVariables'
import type { DeepPartial } from 'ts-essentials'
import type { DURSubject } from 'types'
import { stripTypename } from 'utils'
import { isReal, ErrorText, SearchResultContainer, StyledSearchAutoComplete } from './shared'

interface Props {
  hotKey?: string
  idKey?: string
  label?: string
  onChange: (value: Medispan.Allergy[]) => void
  onCustomInputChange?: (value: CustomDurSubject[]) => void
  value?: DeepPartial<Medispan.Allergy[]>
  customValue?: DeepPartial<CustomDurSubject[]>
}

const DURAllergiesAutoComplete: FC<Props> = ({
  hotKey = 'c',
  idKey,
  onCustomInputChange,
  customValue = [],
  onChange,
  value = [],
  label,
}: Props): JSX.Element => {
  const [searchTerm, debouncedSearchTerm, setSearchTerm] = useStateWithDebouncedValue('', 1000)

  const { data, error } = useQuery<{ getAllergies: DURSubject[] }>(GET_DUR_ALLERGIES, {
    skip: !debouncedSearchTerm,
    variables: { knownAllergies: debouncedSearchTerm },
  })
  const hasError = !!error
  const suggestions = data?.getAllergies.map(stripTypename) ?? []
  return (
    <>
      <StyledSearchAutoComplete
        allowCustomInput
        data-testid="allergies"
        withCapsule
        label={label}
        autoCompleteSuggestions={suggestions}
        hotKey={hotKey}
        placeholder="Search allergies by name…"
        retainFocusAfterSelection
        value={searchTerm}
        idKey={idKey}
        onChange={setSearchTerm}
        suggestionComponent={({
          name,
          highlighted,
          onClick,
          value,
        }: DURSubject & { highlighted: boolean; onClick: () => void }) => (
          <DropDownOption data-testid="suggestion-item" highlighted={highlighted} onClick={onClick}>
            <p>
              {name} {name?.length ? <>({value})</> : value}
            </p>
          </DropDownOption>
        )}
        onCustomInputSelect={(chosen: string) => {
          setSearchTerm('')
          onCustomInputChange?.([...(customValue as CustomDurSubject[]), { name: chosen }])
        }}
        onSelect={chosen => {
          setSearchTerm('')
          onChange(stripTypename([...value, chosen]))
        }}
      >
        {hasError && <ErrorText>Unable to load DUR subjects for allergies</ErrorText>}
      </StyledSearchAutoComplete>
      <SearchResultContainer data-testid="allergies-results">
        {value.filter(isReal).map(({ fromHistory, name, value }, index, arr) => (
          <Chip
            key={`${value}${index}`}
            data-testid={fromHistory ? 'history-item' : 'result-item'}
            state="info"
            size="lg"
            css={{ color: capsulePrimaryColorDark, fontWeight: 'bold', textTransform: 'uppercase' }}
            onDismiss={
              fromHistory ? undefined : () => onChange(arr.filter(({ name: otherName }) => otherName !== name))
            }
          >
            {name}
          </Chip>
        ))}
        {customValue.map((customVal, index, arr) => (
          <Chip
            key={`${customVal?.name}${index}`}
            data-testid={'result-item'}
            state="info"
            size="lg"
            css={{ backgroundColor: lightOrange, color: darkOrange, fontWeight: 'bold', textTransform: 'uppercase' }}
            onDismiss={() =>
              onCustomInputChange?.(arr.filter(other => other?.name !== customVal?.name) as CustomDurSubject[])
            }
          >
            {customVal?.name}
          </Chip>
        ))}
      </SearchResultContainer>
    </>
  )
}
export default DURAllergiesAutoComplete
